import { useCallback, useState } from 'react';

import { ArrowLeft, Braces, Image, RefreshCw, Search, Square } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useCancelStockSync,
  useEnrichStockImages,
  useStockItems,
  useStockSyncRuns,
  useStockSyncState,
  useTriggerStockSync,
} from '@/hooks/useStockSync';

import type { StockItemRow, StockSyncRunRow } from '@/lib/api/stock-sync.api';

const RUN_BADGE: Record<StockSyncRunRow['status'], 'secondary' | 'outline' | 'destructive'> = {
  running: 'outline',
  success: 'secondary',
  failed: 'destructive',
  auth_expired: 'destructive',
};

const Stat = ({ label, value }: { label: string; value: string | number }) => (
  <div>
    <div className="text-muted-foreground text-xs">{label}</div>
    <div className="text-lg font-semibold">{value}</div>
  </div>
);

const itemTitle = (item: StockItemRow): string =>
  [item.make, item.model, item.trim].filter(Boolean).join(' ') ||
  String(item.data?.title ?? item.external_id);

export const StockSourceDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || 1;
  const search = searchParams.get('search') || '';
  const [searchInput, setSearchInput] = useState(search);
  const [jsonItem, setJsonItem] = useState<StockItemRow | null>(null);
  const [imageItem, setImageItem] = useState<StockItemRow | null>(null);
  const [runsPage, setRunsPage] = useState(1);

  const { data: state, isLoading } = useStockSyncState(id);
  const { data: runs } = useStockSyncRuns(id, runsPage);
  const { data: items } = useStockItems(id, page, search || undefined);
  const syncMut = useTriggerStockSync();
  const cancelMut = useCancelStockSync();
  const enrichMut = useEnrichStockImages();

  const updateParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (!value) next.delete(key);
        else next.set(key, value);
        if (key !== 'page') next.delete('page');
        return next;
      });
    },
    [setSearchParams],
  );

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    const timeout = setTimeout(() => updateParam('search', value), 300);
    return () => clearTimeout(timeout);
  };

  if (isLoading || !state) {
    return <p className="text-muted-foreground p-6">Loading…</p>;
  }

  const { source } = state;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/stock-sync">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <h1 className="text-2xl font-bold">{source.name}</h1>
            {state.running && <Badge variant="outline">Syncing…</Badge>}
            {state.enriching_images && <Badge variant="outline">Enriching images…</Badge>}
            {source.status === 'needs_auth' && <Badge variant="destructive">Needs auth</Badge>}
          </div>
          <p className="text-muted-foreground text-sm">{source.description}</p>
        </div>
        <div className="flex gap-2">
          {state.running ? (
            <Button
              variant="outline"
              disabled={cancelMut.isPending}
              onClick={() => id && cancelMut.mutate(id)}
            >
              <Square className="mr-2 h-4 w-4" /> Cancel sync
            </Button>
          ) : (
            <Button
              variant="outline"
              disabled={syncMut.isPending}
              onClick={() => id && syncMut.mutate(id)}
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Sync now
            </Button>
          )}
          <Button
            variant="outline"
            title={`Fill image_url for the ${state.items.missing_images} item(s) missing one (browser sweep, only-missing)`}
            disabled={state.enriching_images || enrichMut.isPending || !state.items.missing_images}
            onClick={() => id && enrichMut.mutate({ id })}
          >
            <Image className="mr-2 h-4 w-4" /> Enrich missing images
            {state.items.missing_images > 0 && ` (${state.items.missing_images})`}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-5">
          <Stat label="Available items" value={state.items.available} />
          <Stat label="Unavailable" value={state.items.unavailable} />
          <Stat label="Missing images" value={state.items.missing_images} />
          <Stat
            label="Last completed"
            value={
              source.last_sync_completed_at
                ? new Date(source.last_sync_completed_at).toLocaleString('en-GB')
                : 'never'
            }
          />
          <Stat label="Last status" value={source.last_sync_status ?? '—'} />
        </CardContent>
      </Card>

      {/* Runs */}
      <div>
        <h2 className="mb-2 text-lg font-semibold">Sync runs</h2>
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Pages</TableHead>
                  <TableHead>Upserted</TableHead>
                  <TableHead>Retired</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!runs?.data.length && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-muted-foreground">
                      No runs yet.
                    </TableCell>
                  </TableRow>
                )}
                {runs?.data.map((run) => (
                  <TableRow key={run.id}>
                    <TableCell className="text-xs">
                      {new Date(run.started_at).toLocaleString('en-GB')}
                    </TableCell>
                    <TableCell>
                      <Badge variant={RUN_BADGE[run.status]}>{run.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">{run.trigger}</TableCell>
                    <TableCell className="text-xs">{run.pages_fetched}</TableCell>
                    <TableCell className="text-xs">{run.upserted}</TableCell>
                    <TableCell className="text-xs">{run.marked_unavailable}</TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate text-xs">
                      {run.error}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        {(runs?.totalPages ?? 0) > 1 && (
          <div className="mt-2 flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={runsPage === 1}
              onClick={() => setRunsPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={runsPage >= (runs?.totalPages ?? 1)}
              onClick={() => setRunsPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Items */}
      <div>
        <h2 className="mb-2 text-lg font-semibold">Stock items</h2>
        <div className="relative mb-3 max-w-sm">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search make, model, trim or id…"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-14">Image</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Year</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Mileage</TableHead>
                  <TableHead>Available</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead className="w-16">Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!items?.data.length && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-muted-foreground">
                      No items synced yet. Run a sync to seed this source.
                    </TableCell>
                  </TableRow>
                )}
                {items?.data.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {item.image_url ? (
                        <button
                          type="button"
                          onClick={() => setImageItem(item)}
                          className="cursor-pointer rounded transition-opacity hover:opacity-80"
                          title="View image"
                        >
                          <img
                            src={item.image_url}
                            alt=""
                            className="h-8 w-12 rounded object-cover"
                            loading="lazy"
                          />
                        </button>
                      ) : (
                        <span className="text-muted-foreground/50 text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.detail_url ? (
                        <a
                          href={item.detail_url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium hover:underline"
                        >
                          {itemTitle(item)}
                        </a>
                      ) : (
                        <span className="font-medium">{itemTitle(item)}</span>
                      )}
                      <div className="text-muted-foreground text-xs">
                        {[item.fuel_type, item.transmission, item.body_type, item.colour]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{item.year ?? '—'}</TableCell>
                    <TableCell className="text-xs">
                      {item.price != null ? `£${item.price.toLocaleString('en-GB')}` : '—'}
                    </TableCell>
                    <TableCell className="text-xs">
                      {item.mileage != null ? item.mileage.toLocaleString('en-GB') : '—'}
                    </TableCell>
                    <TableCell>
                      {item.is_available ? (
                        <Badge variant="secondary">Yes</Badge>
                      ) : (
                        <Badge variant="outline">No</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {new Date(item.last_seen_at).toLocaleDateString('en-GB')}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => setJsonItem(item)}
                        className="text-muted-foreground hover:text-foreground rounded p-1"
                        title="View raw data"
                      >
                        <Braces className="h-4 w-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        {(items?.totalPages ?? 0) > 1 && (
          <div className="mt-2 flex items-center justify-between">
            <p className="text-muted-foreground text-sm">
              Page {items?.page} of {items?.totalPages} ({items?.total} items)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => updateParam('page', String(page - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= (items?.totalPages ?? 1)}
                onClick={() => updateParam('page', String(page + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {imageItem && (
        <Dialog open onOpenChange={(open) => !open && setImageItem(null)}>
          <DialogContent className="sm:max-w-[720px]">
            <DialogHeader>
              <DialogTitle className="text-base">{itemTitle(imageItem)}</DialogTitle>
            </DialogHeader>
            <img
              src={imageItem.image_url ?? undefined}
              alt={itemTitle(imageItem)}
              className="max-h-[70vh] w-full rounded-md object-contain"
            />
            {imageItem.image_url && (
              <a
                href={imageItem.image_url}
                target="_blank"
                rel="noreferrer"
                className="text-muted-foreground truncate text-xs hover:underline"
              >
                {imageItem.image_url}
              </a>
            )}
          </DialogContent>
        </Dialog>
      )}

      {jsonItem && (
        <Dialog open onOpenChange={(open) => !open && setJsonItem(null)}>
          <DialogContent className="sm:max-w-[640px]">
            <DialogHeader>
              <DialogTitle className="text-base">
                Stock item &mdash; {jsonItem.external_id}
              </DialogTitle>
            </DialogHeader>
            <pre className="bg-muted max-h-[460px] overflow-auto rounded-md p-3 font-mono text-xs">
              {JSON.stringify(jsonItem.data ?? {}, null, 2)}
            </pre>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
